<template>
    <div class="user-permissions-tab">
        <div class="search-section">
            <a-input-search
                v-model="searchText"
                :placeholder="$t('message.search')"
                class="search-input"
                @search="onSearch"
            />
        </div>

        <div class="scopes-table">
            <div class="scopes-table-header">
                <div class="header-cell target-header">
                    {{ $t('tables.permissions.targetHeader') }}
                </div>
                <div 
                    v-for="group in scopeGroups" 
                    :key="group.id" 
                    class="header-group"
                >
                    <div class="group-title">
                        <i class="material-icons">{{ group.icon }}</i>
                        {{ $t(group.titleKey) }}
                    </div>
                    <div class="group-scopes">
                        <div 
                            v-for="scope in group.scopes" 
                            :key="scope"
                            class="scope-header"
                            v-tippy="{ content: getScopeDescription(scope) }"
                        >
                            {{ getScopeLabelText(scope) }}
                        </div>
                    </div>
                </div>
            </div>

            <div class="scopes-table-body">
                <!-- Aktif kullanıcılar (Permission'ı olan) - Root kullanıcılar dahil -->
                <div v-if="filteredActiveUsers.length > 0" class="group-section">
                    <div class="group-header active-group">
                        <i class="material-icons">check_circle</i>
                        <span>Yetkili Kullanıcılar ({{ filteredActiveUsers.length }})</span>
                    </div>
                    <UserPermissionRow
                        v-for="(userItem, index) in filteredActiveUsers"
                        :key="`active-user-${userItem.user._id}`"
                        :user="userItem.user"
                        :target="userItem.target"
                        :target-index="userItem.targetIndex"
                        :scope-groups="scopeGroups"
                        :permission-targets="permissionTargets"
                        :departments="departments"
                        :current-user-id="currentUserId"
                        :is-active="true"
                        :is-odd="index % 2 === 1"
                        @toggle-scope="onToggleScope"
                        @deactivate="onDeactivate"
                    />
                </div>

                <!-- İnaktif kullanıcılar (Permission'ı olmayan) -->
                <div v-if="filteredInactiveUsers.length > 0" class="group-section">
                    <div class="group-header inactive-group">
                        <i class="material-icons">radio_button_unchecked</i>
                        <span>Diğer Kullanıcılar ({{ filteredInactiveUsers.length }})</span>
                    </div>
                    <UserPermissionRow
                        v-for="(user, index) in filteredInactiveUsers"
                        :key="`inactive-user-${user._id}`"
                        :user="user"
                        :target="null"
                        :target-index="-1"
                        :scope-groups="scopeGroups"
                        :permission-targets="permissionTargets"
                        :departments="departments"
                        :current-user-id="currentUserId"
                        :is-active="false"
                        :is-odd="index % 2 === 1"
                        @activate="onActivate"
                    />
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import Vue from "vue";
import UserPermissionRow from "./UserPermissionRow.vue";
import { SCOPE_LABELS } from "../../../../../utils/TablePermissionScopes";

export default Vue.extend({
    name: "UserPermissionsTab",
    components: {
        UserPermissionRow,
    },
    props: {
        users: {
            type: Array,
            required: true,
        },
        departments: {
            type: Array,
            required: true,
        },
        permissionTargets: {
            type: Array,
            required: true,
        },
        scopeGroups: {
            type: Array,
            required: true,
        },
        currentUserId: {
            type: String,
            required: true,
        },
    },
    data() {
        return {
            searchText: "",
        };
    },
    computed: {
        activeUsers() {
            const active = [];
            this.permissionTargets.forEach((target, index) => {
                if (target.target === "user" && target.user) {
                    const user = this.users.find((u) => u._id.toString() === target.user.toString());
                    if (user) {
                        active.push({
                            user,
                            target,
                            targetIndex: index,
                        });
                    }
                }
            });
            return active;
        },
        inactiveUsers() {
            const activeUserIds = this.activeUsers.map((item) => item.user._id.toString());
            return this.users.filter((u) => !activeUserIds.includes(u._id.toString()));
        },
        filteredActiveUsers() {
            if (!this.searchText) return this.activeUsers;
            const search = this.searchText.toLowerCase();
            return this.activeUsers.filter((item) => item.user.name.toLowerCase().includes(search));
        },
        filteredInactiveUsers() {
            if (!this.searchText) return this.inactiveUsers;
            const search = this.searchText.toLowerCase();
            return this.inactiveUsers.filter((u) => u.name.toLowerCase().includes(search));
        },
    },
    methods: {
        onSearch(value) {
            this.searchText = value;
        },
        getScopeLabelText(scope) {
            const label = SCOPE_LABELS[scope] || { labelKey: scope };
            return this.$t(label.labelKey);
        },
        getScopeDescription(scope) {
            const label = SCOPE_LABELS[scope] || { descriptionKey: scope };
            return this.$t(label.descriptionKey);
        },
        onToggleScope(payload) {
            this.$emit("toggle-scope", payload);
        },
        onActivate(userId) {
            this.$emit("activate-target", {
                targetType: "user",
                targetId: userId,
            });
        },
        onDeactivate(userId) {
            const targetIndex = this.permissionTargets.findIndex(
                (t) => t.target === "user" && t.user && t.user.toString() === userId.toString()
            );
            if (targetIndex !== -1) {
                this.$emit("activate-target", {
                    targetType: "user",
                    targetId: userId,
                });
            }
        },
    },
});
</script>

<style scoped lang="scss">
.user-permissions-tab {
    .search-section {
        padding: 15px;
        background: white;
        border-bottom: 1px solid var(--color-border-default);

        .search-input {
            width: 100%;
            max-width: 400px;
        }
    }

    .scopes-table {
        border: 1px solid var(--color-border-default);
        border-radius: 8px;
        overflow: hidden;

        .scopes-table-header {
            display: flex;
            background: var(--color-bg-neutral-default);
            border-bottom: 2px solid var(--color-border-default);
            position: sticky;
            top: 0;
            z-index: 10;

            .header-cell {
                padding: 12px;
                font-weight: 600;
                border-right: 1px solid var(--color-border-default);

                &.target-header {
                    width: 250px;
                    min-width: 250px;
                    flex-shrink: 0;
                }
            }

            .header-group {
                flex: 1;
                border-right: 1px solid var(--color-border-default);

                &:last-child {
                    border-right: none;
                }

                .group-title {
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    gap: 5px;
                    padding: 8px;
                    background: var(--color-bg-default);
                    border-bottom: 1px solid var(--color-border-default);
                    font-weight: 600;
                    font-size: 13px;

                    .material-icons {
                        font-size: 16px;
                    }
                }

                .group-scopes {
                    display: flex;

                    .scope-header {
                        flex: 1;
                        padding: 8px 4px;
                        text-align: center;
                        font-size: 12px;
                        font-weight: 500;
                        border-right: 1px solid var(--color-border-subdued);
                        cursor: help;

                        &:last-child {
                            border-right: none;
                        }
                    }
                }
            }
        }

        .scopes-table-body {
            max-height: calc(100vh - 350px);
            overflow-y: auto;

            .group-section {
                .group-header {
                    display: flex;
                    align-items: center;
                    gap: 8px;
                    padding: 12px;
                    background: #fafafa;
                    border-bottom: 2px solid var(--color-border-default);
                    font-weight: 600;
                    font-size: 14px;

                    &.active-group {
                        background: #e6f7ff;
                        color: #0050b3;

                        .material-icons {
                            color: #52c41a;
                        }
                    }

                    &.inactive-group {
                        background: #f5f5f5;
                        color: #666;

                        .material-icons {
                            color: #999;
                        }
                    }
                }
            }
        }
    }
}
</style>

